import React from 'react';
import {
    Box, Button, CircularProgress, Container, Paper, Typography, Link, IconButton, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, Alert, Grid, Chip, Card, CardContent, Divider,
    List, ListItem, ListItemText, Tooltip, Badge
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../AuthProvider';
import {
    getUnmoderatedPubs, getPlaceDetails, approveGarden, deleteGarden, getFullMeshes, getPubGardens
} from '../API';
import TopNav from './TopNav';
import Fonts from './Fonts';
import { Footer } from './Footer';
import axios from 'axios';

// Import components and types for the workflow
import SubmitGarden, { ExistingGarden, FullMeshResponse } from './SubmitGarden';

// Icons
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';

// Interface definitions
interface ModerationQueueItem {
    google_place_id: string;
    date_added: string;
    sub_areas: Array<{ uid: string; name: string; }>;
    first_garden_submitter_name: string;
    first_garden_submitter_type: 'anonymous' | 'registered' | 'unknown' | 'anonymous (no profile link)';
    // NOTE: These fields should be provided by the backend get_unmoderated_pubs API
    // to show the display_name from UserProfile instead of just the submission name
    first_garden_submitter_display_name?: string; // Display name from UserProfile
    first_garden_submitter_profile_id?: string; // Profile ID for additional info
}

interface PubDataForModeration {
    google_place_id: string;
    name: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    original_sub_areas_count?: number;
    first_submitter_name_from_queue?: string;
    first_submitter_type_from_queue?: string;
    first_submitter_display_name_from_queue?: string;
    date_submitted?: string;
}

const ModerationPanel: React.FC = () => {
    const { user, profile, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Core state
    const [unmoderatedPubs, setUnmoderatedPubs] = React.useState<ModerationQueueItem[]>([]);
    const [currentIndex, setCurrentIndex] = React.useState<number>(0);
    const [currentPubData, setCurrentPubData] = React.useState<PubDataForModeration | null>(null);
    const [currentGardens, setCurrentGardens] = React.useState<ExistingGarden[]>([]);
    const [meshData, setMeshData] = React.useState<FullMeshResponse | null>(null);

    // Loading and status states
    const [queueLoading, setQueueLoading] = React.useState<boolean>(true);
    const [pubDetailsLoading, setPubDetailsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [actionMessage, setActionMessage] = React.useState<string | null>(null);
    const [isAuthorized, setIsAuthorized] = React.useState<boolean | null>(null);

    // Dialog state
    const [confirmRejectDialogOpen, setConfirmRejectDialogOpen] = React.useState<boolean>(false);
    const currentlyLoadingPubIdRef = React.useRef<string | null>(null);

    // Authorization check
    React.useEffect(() => {
        if (authLoading) return;
        if (!user || !profile?.is_staff) {
            setIsAuthorized(false);
            setError("Access Denied: You must be logged in as staff.");
            setQueueLoading(false);
            return;
        }

        setQueueLoading(true);
        setError(null);
        getUnmoderatedPubs(1, 20)
            .then(response => {
                setIsAuthorized(true);
                const pubs = response.data?.pubs || [];
                setUnmoderatedPubs(pubs);
                if (pubs.length === 0) {
                    setActionMessage("No pubs currently awaiting moderation.");
                }
            })
            .catch(err => {
                if (axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 401)) {
                    setIsAuthorized(false);
                    setError("Access Denied: You do not have permission.");
                } else {
                    setIsAuthorized(null);
                    setError("Failed to load moderation queue.");
                }
                setUnmoderatedPubs([]);
            })
            .finally(() => setQueueLoading(false));
    }, [authLoading, user]);

    // Load all data for the current pub
    const loadPubDetails = React.useCallback(async (item: ModerationQueueItem | null) => {
        if (!item?.google_place_id) {
            setCurrentPubData(null);
            setMeshData(null);
setCurrentGardens([]);
            return;
        }

        if (currentlyLoadingPubIdRef.current === item.google_place_id && pubDetailsLoading) return;

        currentlyLoadingPubIdRef.current = item.google_place_id;
        setPubDetailsLoading(true);
        setError(null);
        setActionMessage(null);

        try {
            const placeDetailsRes = await getPlaceDetails(item.google_place_id);
            if (!placeDetailsRes.data?.pubs?.[0]) {
                throw new Error("Could not fetch pub details from Google.");
            }
            const googlePubData = placeDetailsRes.data.pubs[0];

            const [meshDataRes, gardensRes] = await Promise.all([
                getFullMeshes(item.google_place_id, googlePubData.latitude, googlePubData.longitude),
                getPubGardens(item.google_place_id)
            ]);

            const pubForDisplay: PubDataForModeration = {
                google_place_id: item.google_place_id,
                name: googlePubData.name || 'Unknown Pub',
                address: googlePubData.address,
                latitude: googlePubData.latitude,
                longitude: googlePubData.longitude,
                original_sub_areas_count: item.sub_areas.length,
                first_submitter_name_from_queue: item.first_garden_submitter_name,
                first_submitter_type_from_queue: item.first_garden_submitter_type,
                first_submitter_display_name_from_queue: item.first_garden_submitter_display_name,
                date_submitted: new Date(item.date_added).toLocaleDateString(),
            };

            setCurrentPubData(pubForDisplay);
            setMeshData(meshDataRes.data);
            
            const gardensDataFromApi = gardensRes.data ? Object.values(gardensRes.data) : [];
            const gardens: ExistingGarden[] = gardensDataFromApi.map((g: any) => ({
                uid: g.uid,
                name: g.sub_area_name || g.name || 'Unnamed Garden',
                polygon: g.garden_latlon || g.polygon || [],
                elevation: g.elevation || 0,
                is_uncertain: g.is_uncertain || false,
                is_owner: false, // Staff can edit all
                submitter_display_name: g.submitter_display_name,
                submitter_name: g.submitter_name,
                submitter_type: g.submitter_type
            }));
            
            setCurrentGardens(gardens);

        } catch (err: any) {
            console.error("Error loading pub details:", err);
            setError(`Failed to load details: ${err.message || 'Unknown error'}`);
            setTimeout(() => handleNext(1, true), 3000); 
        } finally {
            setPubDetailsLoading(false);
            if (currentlyLoadingPubIdRef.current === item.google_place_id) {
                currentlyLoadingPubIdRef.current = null;
            }
        }
    }, [pubDetailsLoading]);

    // Load pub when index changes
    React.useEffect(() => {
        if (isAuthorized === false || isAuthorized === null || queueLoading) return;

        if (unmoderatedPubs.length > 0 && currentIndex < unmoderatedPubs.length) {
            const pubToLoad = unmoderatedPubs[currentIndex];
            if (pubToLoad && (!currentPubData || currentPubData.google_place_id !== pubToLoad.google_place_id)) {
                loadPubDetails(pubToLoad);
            }
        } else if (unmoderatedPubs.length > 0 && currentIndex >= unmoderatedPubs.length) {
            setActionMessage("All pubs reviewed. Reload the page to check for new submissions.");
            setCurrentPubData(null);
            setMeshData(null);
            setCurrentGardens([]);
        }
    }, [currentIndex, unmoderatedPubs, isAuthorized, queueLoading, loadPubDetails, currentPubData]);

    // Navigation handlers
    const handleNext = React.useCallback((skip: number = 1, force: boolean = false) => {
        if (!force && pubDetailsLoading) return;
        setActionMessage(null);
        setError(null);
        setCurrentIndex(prev => Math.min(prev + skip, unmoderatedPubs.length));
    }, [pubDetailsLoading, unmoderatedPubs.length]);
    
    const handlePrevious = () => {
        if (pubDetailsLoading) return;
        setActionMessage(null);
        setError(null);
        setCurrentIndex(prev => Math.max(0, prev - 1));
    };

    const handleRefresh = () => {
        if (unmoderatedPubs[currentIndex]) {
            loadPubDetails(unmoderatedPubs[currentIndex]);
        }
    };

    // Action handlers
    const performActionAndMoveNext = async (actionAsync: () => Promise<void>, successMessage: string, errorMessage: string) => {
        if (!currentPubData || pubDetailsLoading) return;

        setPubDetailsLoading(true);
        try {
            await actionAsync();
            setActionMessage(successMessage);
            setTimeout(() => handleNext(), 1500); // Brief pause to show success message
        } catch (err: any) {
            setError(`${errorMessage} Error: ${err.message || 'Unknown error'}`);
            setPubDetailsLoading(false);
        }
    };

    const handleAccept = async () => {
        const pubName = currentPubData?.name || "Pub";
        await performActionAndMoveNext(
            async () => {
                if (!currentPubData) throw new Error("Current pub data is null");
                await approveGarden(currentPubData.google_place_id);
            },
            `✅ ${pubName} approved successfully.`,
            `Failed to approve ${pubName}.`
        );
    };

    const handleReject = async () => {
        if (!currentPubData) return;
        setConfirmRejectDialogOpen(false);
        const pubName = currentPubData.name;
        await performActionAndMoveNext(
            async () => {
                if (!currentPubData) throw new Error("Current pub data is null");
                // Delete all gardens for this pub
                for (const garden of currentGardens) {
                    await deleteGarden(garden.uid);
                }
                // Mark as moderated to remove from queue
                await approveGarden(currentPubData.google_place_id);
            },
            `❌ Gardens for ${pubName} rejected.`,
            `Failed to reject gardens for ${pubName}.`
        );
    };

    // Callbacks for SubmitGarden
    const handleEditComplete = React.useCallback(() => {
        setActionMessage("Garden updated successfully. Review the changes and click 'Approve' to finalize.");
        // Reload details to show the updated garden
        if (unmoderatedPubs[currentIndex]) {
            loadPubDetails(unmoderatedPubs[currentIndex]);
        }
    }, [currentIndex, unmoderatedPubs, loadPubDetails]);
    
    const handleCancelEditing = React.useCallback(() => {
        setActionMessage("Edit cancelled.");
        // Optionally reload to revert any visual changes if needed
        if (unmoderatedPubs[currentIndex]) {
            loadPubDetails(unmoderatedPubs[currentIndex]);
        }
    }, [currentIndex, unmoderatedPubs, loadPubDetails]);

    // Loading states
    if (authLoading || isAuthorized === null) {
        return <Container sx={{ textAlign: 'center', mt: 5 }}><CircularProgress /></Container>;
    }
    
    if (isAuthorized === false) {
        return (
            <>
                <TopNav title="Moderation Panel" />
                <Container sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error">{error || "Access Denied."}</Typography>
                </Container>
            </>
        );
    }
    
    if (queueLoading && unmoderatedPubs.length === 0) {
        return (
            <>
                <TopNav title="Moderation Panel" />
                <Container sx={{ textAlign: 'center', mt: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading moderation queue...</Typography>
                </Container>
            </>
        );
    }
    
    if (unmoderatedPubs.length === 0 && !queueLoading) {
        return (
            <>
                <TopNav title="Moderation Panel" />
                <Container sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h6">{actionMessage || "No pubs to moderate."}</Typography>
                </Container>
                <Box sx={{ px: '5%', mt: 'auto' }}><Footer /></Box>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>Moderation Panel | Suntap Staff</title>
                <meta name="description" content="Staff moderation panel for reviewing and approving pub garden submissions." />
                <link rel="canonical" href="https://sun-tap.com/moderate" />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Fonts />
            <TopNav title="Moderation Panel" />
            <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                {actionMessage && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>{actionMessage}</Alert>}

                <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <Chip 
                        label={`Queue: ${currentIndex + 1} of ${unmoderatedPubs.length}`} 
                        color="primary" 
                        sx={{ fontSize: '1rem' }}
                    />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} lg={4}>
                        <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {currentPubData ? (
                                <>
                                    <Box>
                                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                            {currentPubData.name}
                                        </Typography>
                                        
                                        <Divider sx={{ my: 2 }} />
                                        
                                        <List dense>
                                            <ListItem>
                                                <ListItemText 
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <PlaceIcon fontSize="small" />
                                                            <Typography variant="body2">Google Place ID</Typography>
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                                                {currentPubData.google_place_id}
                                                            </Typography>
                                                            <IconButton 
                                                                size="small" 
                                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentPubData.address || currentPubData.name || '')}&query_place_id=${currentPubData.google_place_id}`}
                                                                target="_blank"
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText 
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AccessTimeIcon fontSize="small" />
                                                            <Typography variant="body2">Submitted</Typography>
                                                        </Box>
                                                    }
                                                    secondary={currentPubData.date_submitted}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText 
                                                    primary="Submitter"
                                                    secondary={
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {currentPubData.first_submitter_type_from_queue === 'registered' ? (
                                                                    <PersonIcon fontSize="small" color="success" />
                                                                ) : (
                                                                    <VpnKeyIcon fontSize="small" color="warning" />
                                                                )}
                                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                    {currentPubData.first_submitter_display_name_from_queue || 
                                                                     currentPubData.first_submitter_name_from_queue || 
                                                                     'Unknown'}
                                                                </Typography>
                                                                <Chip 
                                                                    label={currentPubData.first_submitter_type_from_queue}
                                                                    size="small"
                                                                    color={currentPubData.first_submitter_type_from_queue === 'registered' ? 'success' : 'warning'}
                                                                    variant="outlined"
                                                                />
                                                            </Box>
                                                            {currentPubData.first_submitter_display_name_from_queue && 
                                                             currentPubData.first_submitter_name_from_queue && 
                                                             currentPubData.first_submitter_display_name_from_queue !== currentPubData.first_submitter_name_from_queue && (
                                                                <Box sx={{ ml: 3 }}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Submission name: {currentPubData.first_submitter_name_from_queue}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        </List>
                                        
                                        <Divider sx={{ my: 2 }} />
                                        
                                        <Typography variant="h6" gutterBottom>
                                            Submitted Gardens ({currentGardens.length})
                                        </Typography>
                                        <List dense sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {currentGardens.length > 0 ? currentGardens.map((garden, idx) => (
                                                <ListItem key={garden.uid}>
                                                    <ListItemText 
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="body2">
                                                                    {idx + 1}. {garden.name}
                                                                </Typography>
                                                                {garden.is_uncertain && (
                                                                    <Tooltip title="Garden boundaries are uncertain">
                                                                        <WarningIcon fontSize="small" color="warning" />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                <Typography variant="caption">
                                                                    {garden.polygon.length} vertices, elevation: {garden.elevation?.toFixed(1) || 0}m
                                                                </Typography>
                                                                {(garden as any).submitter_display_name && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        By: {(garden as any).submitter_display_name}
                                                                        {(garden as any).submitter_name && 
                                                                         (garden as any).submitter_display_name !== (garden as any).submitter_name && 
                                                                         ` (${(garden as any).submitter_name})`}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            )) : (
                                                <ListItem>
                                                    <ListItemText primary="No gardens found for this submission." />
                                                </ListItem>
                                            )}
                                        </List>
                                    </Box>

                                    <Box sx={{ mt: 'auto' }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Button 
                                                    fullWidth 
                                                    variant="contained" 
                                                    color="success" 
                                                    startIcon={<CheckCircleOutlineIcon />} 
                                                    onClick={handleAccept} 
                                                    disabled={pubDetailsLoading}
                                                    size="large"
                                                >
                                                    Approve
                                                </Button>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button 
                                                    fullWidth 
                                                    variant="outlined" 
                                                    color="error" 
                                                    startIcon={<CancelIcon />} 
                                                    onClick={() => setConfirmRejectDialogOpen(true)} 
                                                    disabled={pubDetailsLoading}
                                                >
                                                    Reject & Delete Gardens
                                                </Button>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Button fullWidth variant="outlined" startIcon={<SkipPreviousIcon />} onClick={handlePrevious} disabled={currentIndex === 0 || pubDetailsLoading}>Prev</Button>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Button fullWidth variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={pubDetailsLoading}>Refresh</Button>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Button fullWidth variant="outlined" startIcon={<SkipNextIcon />} onClick={() => handleNext()} disabled={(currentIndex >= unmoderatedPubs.length - 1) || pubDetailsLoading}>Skip</Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 5, m: 'auto' }}>
                                    <CircularProgress />
                                    <Typography sx={{ mt: 2 }}>Loading pub details...</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} lg={8}>
                        <Paper elevation={3} sx={{ height: '75vh', overflow: 'hidden', position: 'relative' }}>
                            {pubDetailsLoading && (
                                <Box sx={{ 
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10
                                }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            
                            {!pubDetailsLoading && currentPubData && (
                                <SubmitGarden
                                    key={currentPubData.google_place_id} // Force remount on pub change
                                    googlePlaceId={currentPubData.google_place_id}
                                    onExitAndRefresh={handleEditComplete}
                                    onCancel={handleCancelEditing}
                                    latitude={currentPubData.latitude}
                                    longitude={currentPubData.longitude}
                                    propExistingGardens={currentGardens}
                                    reusableMeshData={meshData}
                                    isModerating={true} // Critical prop for simplified UI
                                />
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                <Dialog open={confirmRejectDialogOpen} onClose={() => setConfirmRejectDialogOpen(false)}>
                    <DialogTitle>Confirm Rejection</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to reject this submission for "{currentPubData?.name}"? 
                            This will delete all submitted gardens for this pub and remove it from the queue. 
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmRejectDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleReject} color="error" variant="contained">
                            Confirm Reject
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
            
            <Box sx={{ mt: 4 }}><Footer /></Box>
        </>
    );
};

export default ModerationPanel;